import { useQuery, useZero } from '@rocicorp/zero/react'
import { useAuth } from '../auth/AuthContext.tsx'
import { unpaidDrinksTotal, unpaidFeesTotal, unusedCreditsTotal } from '../lib/balance.ts'
import { formatCents, formatTime } from '../lib/format.ts'
import { mutators } from '../zero/mutators.ts'
import { queries } from '../zero/queries.ts'
import { BalanceCard } from './BalanceCard.tsx'
import { CollapsibleSection } from './CollapsibleSection.tsx'
import { GuestList } from './GuestList.tsx'
import { CreditHistory } from './history/CreditHistory.tsx'
import { DrinkHistory } from './history/DrinkHistory.tsx'
import { FeeHistory } from './history/FeeHistory.tsx'
import { PaymentHistory } from './history/PaymentHistory.tsx'

/**
 * A member's tab (US-12 and US-13): live balance, the outstanding working list,
 * guest management, then the four paginated histories. Reused by the member's
 * own page and the admin drill-down; everything updates reactively.
 *
 * The balance queries stay unbounded because they carry only OPEN rows — a
 * settled member syncs nothing. Paid history is the part that grows without
 * limit, and that is what pages.
 */
export function UserTab({ userId }: { userId: string }) {
  const z = useZero()
  const { session } = useAuth()
  const [outstandingDrinks] = useQuery(queries.outstandingDrinksForUser({ userId }))
  const [outstandingFees] = useQuery(queries.outstandingFeesForUser({ userId }))
  const [credits] = useQuery(queries.unusedCreditsForUser({ userId }))

  const canManage =
    !!session && (session.auth.role === 'admin' || session.auth.sub === userId)
  const isAdmin = session?.auth.role === 'admin'

  async function removeDrink(id: string) {
    try {
      await z.mutate(mutators.drinks.remove({ id }))
    } catch {
      /* optimistic rollback handled by Zero */
    }
  }

  return (
    <>
      <BalanceCard
        drinksTotal={unpaidDrinksTotal(outstandingDrinks)}
        feesTotal={unpaidFeesTotal(outstandingFees)}
        creditTotal={unusedCreditsTotal(credits)}
      />

      <CollapsibleSection title="Outstanding drinks" count={outstandingDrinks.length}>
        <div className="card">
          {outstandingDrinks.length === 0 ? (
            <p className="empty">All paid up. 🍺</p>
          ) : (
            outstandingDrinks.map((d) => (
              <div className="line" key={d.id}>
                <div className="line-main">
                  <div className="line-title">
                    {d.drink?.description ?? 'Drink'}
                    {d.guest ? <span className="muted"> · for {d.guest.name}</span> : null}
                  </div>
                  <div className="line-sub">{formatTime(d.purchasedAt)}</div>
                </div>
                <span className="line-amount">{formatCents(d.drink?.priceCents ?? 0)}</span>
                {canManage ? (
                  <button className="btn-ghost" onClick={() => removeDrink(d.id)} title="Remove">
                    ✕
                  </button>
                ) : null}
              </div>
            ))
          )}
        </div>
      </CollapsibleSection>

      {canManage ? <GuestList userId={userId} /> : null}

      <DrinkHistory userId={userId} />
      <FeeHistory userId={userId} canRemove={isAdmin} />
      <CreditHistory userId={userId} />
      <PaymentHistory userId={userId} />
    </>
  )
}
